import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getSurveyMeta, SurveyMeta } from 'src/api/dataVault/survey';

type Status = 'idle' | 'loading' | 'succeeded' | 'failed';

interface Entry {
  status: Status;
  data: SurveyMeta | null;
  error: string | null;
  lastFetchedAt: number | null;
}

interface State {
  bySurveyId: Record<number, Entry>;
}

const STALE_MS = 5 * 60_000;
const initialState: State = { bySurveyId: {} };

export const fetchSurveyMeta = createAsyncThunk(
  'surveyMeta/fetch',
  async (
    {
      surveyId,
      actingOnMemberId,
      forceRefresh = false,
    }: {
      surveyId: number;
      actingOnMemberId?: number | null;
      forceRefresh?: boolean;
    },
    { getState, rejectWithValue }
  ) => {
    const state = (getState() as { surveyMeta: State }).surveyMeta;
    const entry = state.bySurveyId[surveyId];
    if (
      !forceRefresh &&
      entry?.status === 'loading' &&
      entry.lastFetchedAt &&
      Date.now() - entry.lastFetchedAt < STALE_MS
    ) {
      return null;
    }
    // Note: 'loading' check above handles case where pending already ran
    // Check persisted succeeded state via lastFetchedAt (pending preserves it)
    const lastFetchedAt = entry?.lastFetchedAt;
    if (
      !forceRefresh &&
      lastFetchedAt &&
      Date.now() - lastFetchedAt < STALE_MS
    ) {
      return null;
    }
    try {
      return await getSurveyMeta(surveyId, { actingOnMemberId });
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      return rejectWithValue(
        err?.response?.data?.detail ?? 'Failed to load survey metadata'
      );
    }
  }
);

const slice = createSlice({
  name: 'surveyMeta',
  initialState,
  reducers: {
    invalidateSurvey(state, action: PayloadAction<number>) {
      delete state.bySurveyId[action.payload];
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchSurveyMeta.pending, (state, action) => {
        const id = action.meta.arg.surveyId;
        const entry = state.bySurveyId[id];
        state.bySurveyId[id] = {
          status: 'loading',
          data: entry?.data ?? null,
          error: null,
          lastFetchedAt: entry?.lastFetchedAt ?? null,
        };
      })
      .addCase(fetchSurveyMeta.fulfilled, (state, action) => {
        const id = action.meta.arg.surveyId;
        if (action.payload === null) {
          return;
        }
        state.bySurveyId[id] = {
          status: 'succeeded',
          data: action.payload,
          error: null,
          lastFetchedAt: Date.now(),
        };
      })
      .addCase(fetchSurveyMeta.rejected, (state, action) => {
        const id = action.meta.arg.surveyId;
        state.bySurveyId[id] = {
          status: 'failed',
          data: null,
          error: action.payload as string,
          lastFetchedAt: null,
        };
      });
  },
});

export const { invalidateSurvey } = slice.actions;

export const selectSurveyMeta =
  (surveyId: number) =>
  (state: { surveyMeta: State }): Entry | undefined =>
    state.surveyMeta.bySurveyId[surveyId];

export default slice.reducer;
